import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useId, useState, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@skatteetaten/ds-buttons';
import { dsI18n, getCommonClassNameDefault, getCommonAutoCompleteDefault, getCommonFormVariantDefault } from '@skatteetaten/ds-core-utils';
import { CancelSVGpath, SearchIcon } from '@skatteetaten/ds-icons';
import { ErrorMessage } from '../ErrorMessage/index.esm.js';
import { LabelWithHelp } from '../LabelWithHelp/index.esm.js';
import './styles.css';

import SearchFieldResult from '../SearchFieldResult/index.esm.js';
import '../Help/index.esm.js';
import './styles.css';


const getSearchFieldHasSearchButtonIconDefault = ()=>true;
const getSearchFieldHideLabelDefault = ()=>true;

const searchInList = (options, filterText)=>{
    const regex = /[\s.,:-]+/g;
    return options.filter((option)=>{
        return (option.title ?? option.description).replace(regex, '').toLowerCase().indexOf(filterText.replace(regex, '').toLowerCase()) > -1;
    }).map((option)=>option);
};

var styles = {
    "searchContainer": "SearchField-module_searchContainer__5gC00",
    "topContainer": "SearchField-module_topContainer__5zv8o",
    "topContainer_large": "SearchField-module_topContainer_large__J1hfX",
    "topContainer_extraLarge": "SearchField-module_topContainer_extraLarge__gGaRO",
    "clearButton": "SearchField-module_clearButton__QMneH",
    "inputWrapper": "SearchField-module_inputWrapper__KxF-Q",
    "input": "SearchField-module_input__KAe-y",
    "searchButton": "SearchField-module_searchButton__4Cfjy",
    "icon": "SearchField-module_icon__BdBZV",
    "searchButton_large": "SearchField-module_searchButton_large__SMqmg",
    "searchButton_extraLarge": "SearchField-module_searchButton_extraLarge__1RDlP",
    "searchResultContainer": "SearchField-module_searchResultContainer__wTlbR",
    "srOnly": "SearchField-module_srOnly__YxEtc"
};

const SearchField = /*#__PURE__*/ forwardRef(({ id: externalId, className = getCommonClassNameDefault(), classNames, lang, 'data-testid': dataTestId, clearButtonTitle, defaultValue, description, errorMessage, helpSvgPath, helpText, label, titleHelpSvg, searchButtonTitle, variant = getCommonFormVariantDefault(), autoComplete = getCommonAutoCompleteDefault(), accessKey, disabled, form, name, placeholder, readOnly, required, showRequiredMark, value, hasSearchButtonIcon = getSearchFieldHasSearchButtonIconDefault(), hideLabel = getSearchFieldHideLabelDefault(), onBlur, onChange, onFocus, onHelpToggle, onSearch, onSearchClick, onClear, onResultClick, results }, ref)=>{
    const listboxRef = useRef(null);
    const inputRef = useRef(null);
    const { t } = useTranslation('ds_forms', {
        i18n: dsI18n
    });
    const generatedId = useId();
    const searchFieldId = externalId ?? `searchField-${generatedId}`;
    const errorId = `searchFieldErrorId-${useId()}`;
    const inputId = `${searchFieldId}-input`;
    const resultsId = `${searchFieldId}-results`;
    const srFocusId = `${searchFieldId}-srFocus`;
    const labelId = `${searchFieldId}-label`;
    const [shouldShowResults, setShowResults] = useState(false);
    const [showClearButton, setShowClearButton] = useState(!!defaultValue || !!value);
    const [currentFocus, setCurrentFocus] = useState(-1);
    useImperativeHandle(ref, ()=>inputRef?.current);
    //skjule clearButton dersom value fjernes programatisk fra utenfor komponenten
    useEffect(()=>{
        if (!value && !defaultValue) {
            setShowClearButton(false);
        }
    }, [
        value,
        defaultValue
    ]);
    useEffect(()=>{
        setShowResults(!!(!disabled && results?.length));
    }, [
        disabled,
        results
    ]);
    useEffect(()=>{
        if (!shouldShowResults) {
            return;
        }
        const handleOutsideMenuEvent = (event)=>{
            const node = event.target;
            if (!listboxRef?.current?.contains(node) && node !== inputRef.current) {
                setShowResults(false);
                setCurrentFocus(-1);
                event.type === 'click' && listboxRef?.current?.focus();
            }
        };
        document.addEventListener('focusin', handleOutsideMenuEvent);
        document.addEventListener('click', handleOutsideMenuEvent);
        return ()=>{
            document.removeEventListener('click', handleOutsideMenuEvent);
            document.removeEventListener('focusin', handleOutsideMenuEvent);
        };
    }, [
        shouldShowResults
    ]);
    const handleKeyDown = (e)=>{
        if (!shouldShowResults) {
            // slik at currentFocus ikke blir flyttet inn i lista hvis man trykker på piltaster og lista er lukket
            return;
        }
        const length = results?.length ?? 0;
        switch(e.key){
            case 'Escape':
                setCurrentFocus(-1);
                setShowResults(false);
                inputRef.current?.focus();
                break;
            case 'ArrowUp':
                e.preventDefault();
                setCurrentFocus((currentFocus)=>currentFocus === 0 ? length - 1 : currentFocus - 1);
                break;
            case 'ArrowDown':
                e.preventDefault();
                setCurrentFocus((currentFocus)=>currentFocus === length - 1 ? 0 : currentFocus + 1);
                break;
        }
    };
    const isLarge = variant === 'large';
    const isExtraLarge = variant === 'extraLarge';
    const searchButtonClassName = `${styles.searchButton} ${isLarge ? styles.searchButton_large : ''} ${isExtraLarge ? styles.searchButton_extraLarge : ''}`.trim();
    const containerClassName = `${styles.topContainer} ${isLarge ? styles.topContainer_large : ''} ${isExtraLarge ? styles.topContainer_extraLarge : ''} ${className} ${classNames?.container ?? ''}`.trim();
    return /*#__PURE__*/ jsxs("div", {
        id: searchFieldId,
        className: containerClassName,
        lang: lang,
        onKeyDown: handleKeyDown,
        children: [
            /*#__PURE__*/ jsx(LabelWithHelp, {
                id: labelId,
                classNames: classNames,
                htmlFor: inputId,
                hideLabel: hideLabel,
                description: description,
                helpSvgPath: helpSvgPath,
                helpText: helpText,
                titleHelpSvg: titleHelpSvg,
                showRequiredMark: showRequiredMark,
                onHelpToggle: onHelpToggle,
                children: label
            }),
            /*#__PURE__*/ jsxs("div", {
                className: `${styles.searchContainer} ${classNames?.searchContainer ?? ''}`.trim(),
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: styles.inputWrapper,
                        children: [
                            /*#__PURE__*/ jsx("span", {
                                id: srFocusId,
                                className: styles.srOnly,
                                children: t('searchfield.Focus')
                            }),
                            /*#__PURE__*/ jsx("input", {
                                ref: inputRef,
                                id: inputId,
                                className: `${styles.input} ${classNames?.textbox ?? ''}`.trim(),
                                "data-testid": dataTestId,
                                accessKey: accessKey,
                                defaultValue: defaultValue,
                                disabled: disabled,
                                form: form,
                                name: name,
                                placeholder: placeholder,
                                readOnly: readOnly,
                                value: value,
                                autoComplete: autoComplete,
                                required: required,
                                "aria-describedby": `${errorMessage ? `${errorId} ` : ''}${srFocusId}`.trim(),
                                "aria-invalid": !!errorMessage || undefined,
                                "aria-owns": shouldShowResults ? resultsId : undefined,
                                type: 'search',
                                onKeyDown: (event)=>{
                                    if (event.key === 'Enter') {
                                        onSearch?.(event);
                                    }
                                },
                                onBlur: onBlur,
                                onChange: (event)=>{
                                    onChange?.(event);
                                    // Nødvendig for at clearButton skal vises riktig for uncontrolled komponent
                                    if (event.target.value.length) {
                                        setShowClearButton(true);
                                    } else {
                                        setShowClearButton(false);
                                    }
                                },
                                onFocus: onFocus
                            }),
                            /*#__PURE__*/ jsx(ErrorMessage, {
                                id: errorId,
                                showError: !!errorMessage,
                                className: classNames?.errorMessage,
                                children: errorMessage
                            }),
                            /*#__PURE__*/ jsx("span", {
                                "aria-live": 'assertive',
                                className: styles.srOnly,
                                children: shouldShowResults && t('searchfield.NumberOfResults', {
                                    ant: results?.length ?? 0
                                })
                            }),
                            shouldShowResults && /*#__PURE__*/ jsx("div", {
                                children: /*#__PURE__*/ jsx("ul", {
                                    ref: listboxRef,
                                    id: resultsId,
                                    className: styles.searchResultContainer,
                                    role: 'listbox',
                                    "aria-labelledby": labelId,
                                    children: results?.map((result, index)=>{
                                        const hasFocus = currentFocus === index;
                                        return /*#__PURE__*/ jsx(SearchFieldResult, {
                                            hasFocus: hasFocus,
                                            "aria-selected": hasFocus,
                                            role: 'option',
                                            title: result.title,
                                            setFocus: setCurrentFocus,
                                            index: index,
                                            onClick: ()=>onResultClick?.(result),
                                            children: result.description
                                        }, result.key ?? result.description);
                                    })
                                })
                            }),
                            showClearButton && !disabled && !readOnly && /*#__PURE__*/ jsx(IconButton, {
                                type: 'reset',
                                className: styles.clearButton,
                                size: variant === 'medium' ? 'extraSmall' : 'small',
                                svgPath: CancelSVGpath,
                                title: clearButtonTitle ?? t('searchfield.ClearButtonTitle'),
                                onClick: (event)=>{
                                    onClear?.(event);
                                    // Nødvendig for å fjerne knappen også i uncontrolled SearchField
                                    setShowClearButton(false);
                                    if (!value && inputRef.current) {
                                        inputRef.current.value = '';
                                    }
                                    inputRef.current?.focus();
                                }
                            })
                        ]
                    }),
                    !readOnly && /*#__PURE__*/ jsx("button", {
                        className: searchButtonClassName,
                        disabled: disabled,
                        onClick: (event)=>{
                            onSearchClick?.(event);
                        },
                        children: hasSearchButtonIcon ? /*#__PURE__*/ jsx(SearchIcon, {
                            className: styles.icon,
                            title: searchButtonTitle ?? t('searchfield.ButtonTitle')
                        }) : searchButtonTitle ?? t('searchfield.ButtonTitle')
                    })
                ]
            })
        ]
    });
});
SearchField.displayName = 'SearchField';
SearchField.Result = SearchFieldResult;
SearchField.Result.displayName = 'SearchField.Result';

export { SearchField, getSearchFieldHasSearchButtonIconDefault, searchInList };
